import React from 'react';
import { PageBanner } from '../components/PageBanner';
import { ContactCTA } from '../components/ContactCTA';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { HeartIcon, ClockIcon, ShieldCheckIcon, SunIcon, CalendarIcon, CheckCircleIcon } from 'lucide-react';
const CAREGIVER_IMG = 'https://cdn.magicpatterns.com/uploads/2kGX94xbQuji2osUz8iKU6/1.jpg';
const SENIORS_IMG = 'https://cdn.magicpatterns.com/uploads/d6jS4Kkp3VskdBgjLTMsA2/3.jpg';
export function RespiteCarePage() {
  const {
    ref: introRef,
    isVisible: isIntroVisible
  } = useScrollAnimation();
  const {
    ref: benefitsRef,
    isVisible: isBenefitsVisible
  } = useScrollAnimation();
  const {
    ref: detailsRef,
    isVisible: isDetailsVisible
  } = useScrollAnimation();
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useScrollAnimation();
  return <main data-id="element-847">
      <PageBanner title="Respite & Holiday Care" subtitle="Take the break you deserve while your loved one receives compassionate, professional care." breadcrumbs={[{
      label: 'Services',
      path: '/services'
    }, {
      label: 'Respite & Holiday Care',
      path: '/services/respite-care'
    }]} data-id="element-848" />

      {/* Intro Section */}
      <section className="py-16 md:py-20 bg-white" data-id="element-849">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-850">
          <div ref={introRef} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center transition-all duration-1000 ease-out ${isIntroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-851">
            <div data-id="element-852">
              <div className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-6" data-id="element-853">
                <HeartIcon className="h-4 w-4 text-primary" data-id="element-854" />
                <span className="text-primary text-sm font-semibold" data-id="element-855">
                  Caregiver Support
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6 leading-tight" data-id="element-856">
                You Deserve a Break — We're Here to Help
              </h2>
              <div className="space-y-5 text-dark-muted text-lg leading-relaxed" data-id="element-857">
                <p data-id="element-858">
                  As a family member in the role of caregiver, you may
                  experience extreme stress. The demands of your own family,
                  coupled with the needs of a parent or loved one can overwhelm.{' '}
                  <strong className="text-dark" data-id="element-859">
                    Take time off and refresh yourself!
                  </strong>
                </p>
                <p data-id="element-860">
                  Our Holiday and Respite Care service will enable you to take a
                  vacation, go away for the holidays, or even just take an
                  afternoon per week off. Edvin Home Healthcare Solutions
                  certified nursing assistants or companions can care for your
                  loved one to the extent needed, allowing you to take the "time
                  out" you need.
                </p>
              </div>

              {/* RN Badge */}
              <div className="mt-8 flex items-center bg-primary/5 border border-primary/10 rounded-xl p-5" data-id="element-861">
                <ShieldCheckIcon className="h-10 w-10 text-primary mr-4 flex-shrink-0" data-id="element-862" />
                <div data-id="element-863">
                  <h4 className="font-bold text-dark" data-id="element-864">RN Supervised — 24/7</h4>
                  <p className="text-sm text-dark-muted" data-id="element-865">
                    All care providers are supervised by a RN Case Manager, who
                    is on-call 24 hours a day, 7 days a week.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden shadow-card-hover" data-id="element-866">
              <img src={CAREGIVER_IMG} alt="Caregiver providing compassionate care to elderly couple" className="w-full h-80 lg:h-[420px] object-cover" data-id="element-867" />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-20 bg-light-alt" data-id="element-868">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-869">
          <div ref={benefitsRef} className={`transition-all duration-1000 ease-out ${isBenefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-870">
            <div className="text-center mb-14" data-id="element-871">
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4" data-id="element-872">
                A Day Off With No Worries
              </h2>
              <div className="w-16 h-1.5 bg-secondary mx-auto mb-6 rounded-full" data-id="element-873"></div>
              <p className="text-lg text-dark-muted max-w-3xl mx-auto" data-id="element-874">
                The caregivers of Edvin Home Healthcare Solutions are ready to
                watch over your loved one in the same considerate way you do,
                giving you a chance to get away.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-875">
              {[{
              icon: SunIcon,
              title: 'Vacation & Travel',
              desc: 'Go on vacation or travel for the holidays knowing your loved one is in safe, caring hands.'
            }, {
              icon: CalendarIcon,
              title: 'Flexible Scheduling',
              desc: 'We provide respite care daily, weekly, or as needed — from a few hours to 24-hour care.'
            }, {
              icon: HeartIcon,
              title: 'Peace of Mind',
              desc: "Count on us for the break you need and complete peace of mind while you're away."
            }, {
              icon: ClockIcon,
              title: 'Any Duration',
              desc: 'Few hours, overnight, 1 hour to 24 hours — we arrange care the way you need it.'
            }].map((benefit, idx) => <div key={idx} className="bg-white rounded-2xl p-7 shadow-card hover:shadow-card-hover border border-gray-100 transition-all duration-300 transform hover:-translate-y-1 group" data-id="element-876">
                  <div className="bg-primary/10 w-14 h-14 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary transition-colors duration-300" data-id="element-877">
                    <benefit.icon className="h-7 w-7 text-primary group-hover:text-white transition-colors duration-300" data-id="element-878" />
                  </div>
                  <h3 className="text-lg font-bold text-dark mb-3" data-id="element-879">
                    {benefit.title}
                  </h3>
                  <p className="text-dark-muted text-sm leading-relaxed" data-id="element-880">
                    {benefit.desc}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Details Section */}
      <section className="py-16 md:py-20 bg-white" data-id="element-881">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-882">
          <div ref={detailsRef} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center transition-all duration-1000 ease-out ${isDetailsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-883">
            <div className="order-2 lg:order-1 rounded-2xl overflow-hidden shadow-card-hover" data-id="element-884">
              <img src={SENIORS_IMG} alt="Happy seniors enjoying life together" className="w-full h-80 lg:h-[400px] object-cover" data-id="element-885" />
            </div>

            <div className="order-1 lg:order-2" data-id="element-886">
              <h2 className="text-3xl font-bold text-dark mb-6" data-id="element-887">
                Personalized to Meet Your Needs
              </h2>
              <p className="text-dark-muted text-lg leading-relaxed mb-8" data-id="element-888">
                Our Respite Care program is a solution when you go on vacation,
                travel for holidays, attend parties, or just need to run a few
                errands. You can count on us for the break you need and peace of
                mind while you're away.
              </p>

              <h3 className="text-xl font-bold text-dark mb-4" data-id="element-889">
                Get the Break You Need
              </h3>
              <p className="text-dark-muted text-lg leading-relaxed mb-8" data-id="element-890">
                Edvin Home Healthcare Solutions gives you the time to rediscover
                yourself, pursue activities that give you joy, or simply relax.
                While Respite Care can't change your loved one's situation, it
                can help you maintain your sense of well-being and significantly
                improve your ability to deal with every situation.
              </p>

              <div className="space-y-3" data-id="element-891">
                {['Certified nursing assistants & companions', 'Personalized care plans for your loved one', 'Daily, weekly, or as-needed scheduling', 'Overnight and 24-hour care available', 'RN Case Manager on-call 24/7', 'Holiday and vacation coverage'].map((item, idx) => <div key={idx} className="flex items-center" data-id="element-892">
                    <CheckCircleIcon className="h-5 w-5 text-secondary mr-3 flex-shrink-0" data-id="element-893" />
                    <span className="text-dark font-medium" data-id="element-894">{item}</span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-light-alt" data-id="element-895">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-896">
          <div ref={ctaRef} className={`bg-primary rounded-3xl p-10 md:p-14 text-center shadow-xl relative overflow-hidden transition-all duration-1000 ease-out ${isCtaVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'}`} data-id="element-897">
            <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(#ffffff 2px, transparent 2px)',
            backgroundSize: '30px 30px'
          }} data-id="element-898"></div>
            <div className="relative z-10" data-id="element-899">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{
              fontFamily: "'Cinzel', serif"
            }} data-id="element-900">
                Ready for a Well-Deserved Break?
              </h2>
              <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-901">
                Contact us today to discuss your respite care needs. We'll
                create a personalized plan so you can take time off with
                complete peace of mind.
              </p>
              <a href="#/contact" className="inline-block bg-white text-primary-dark hover:bg-light-alt px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1" data-id="element-902">
                Contact Us Today
              </a>
            </div>
          </div>
        </div>
      </section>

      <ContactCTA data-id="element-903" />
    </main>;
}